/* Altara CLOSING slide (~14s) — memorable payoff.
 * Beat 1: "One consumer. One profile. Continuously enriched." — three lines build
 * Beat 2: three audience payoffs slide in — consumers · institutions · regulators
 * Beat 3: tagline reveal — TRUST BEFORE TRANSACTION.  Gold sweep.
 * Beat 4: signature — Altara Consumer Intelligence · IOSCO TechSprint 2026 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { Play, RotateCcw, User, Building2, Landmark } from "lucide-react";

export default function WalletAltaraClosingDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") !== "0";
  if (embed) {
    return (
      <div className="w-screen h-screen relative overflow-hidden" data-testid="altara-closing-embed"
           style={{ background: `radial-gradient(1200px 800px at 50% 40%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
        <StarField />
        <ClosingStage autoplay={autoplay} />
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8" data-testid="altara-closing-demo">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: GOLD }}>Slide asset</div>
          <h1 className="font-display text-3xl font-semibold" style={{ color: NAVY }}>The Closing</h1>
        </div>
        <div className="relative rounded-3xl overflow-hidden shadow-2xl" style={{ minHeight: 640,
              background: `radial-gradient(1200px 800px at 50% 40%, #142958 0%, #0A1F44 50%, #05132C 100%)` }}>
          <StarField />
          <ClosingStage autoplay />
        </div>
      </div>
    </WalletShell>
  );
}

function SlideBrandMark() {
  return (
    <div className="absolute bottom-4 right-6 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: GOLD, opacity: 0.4 }}>
      altara.co
    </div>
  );
}

/* Subtle animated pinpoints for cinematic feel. */
function StarField() {
  const stars = useRef(null);
  if (!stars.current) {
    stars.current = Array.from({ length: 40 }).map((_, i) => ({
      x: Math.random() * 100, y: Math.random() * 100,
      s: 0.5 + Math.random() * 1.5,
      d: Math.random() * 5,
    }));
  }
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      {stars.current.map((s, i) => (
        <motion.span key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.5, 0.2, 0.5, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, delay: s.d }}
          className="absolute rounded-full"
          style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s, background: GOLD }} />
      ))}
    </div>
  );
}

function ClosingStage({ autoplay }) {
  const [phase, setPhase] = useState(0); // 0 idle · 1 lines · 2 audiences · 3 tagline · 4 signature
  const timers = useRef([]);
  const fired = useRef(false);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));

  const play = () => {
    if (phase > 0 && phase < 4) return;
    clear(); setPhase(1);
    t(4500,  () => setPhase(2));
    t(9000,  () => setPhase(3));
    t(12500, () => setPhase(4));
  };
  const reset = () => { clear(); setPhase(0); };

  useEffect(() => {
    if (!autoplay || fired.current) return;
    fired.current = true;
    setTimeout(play, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => clear(), []);

  return (
    <div className="absolute inset-0 flex items-center justify-center px-10">
      <AnimatePresence mode="wait">
        {phase === 0 && (
          <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: GOLD }}>
              The Closing
            </div>
            <button onClick={play} data-testid="play-closing"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold"
                    style={{ background: GOLD, color: NAVY }}>
              <Play className="w-4 h-4" /> Play
            </button>
          </motion.div>
        )}

        {phase === 1 && (
          <motion.div key="lines" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                      className="text-center max-w-4xl">
            <BuildLine text="One consumer." delay={0.2} />
            <BuildLine text="One profile." delay={1.2} />
            <BuildLine text="Continuously enriched." delay={2.2} gold />
          </motion.div>
        )}

        {phase === 2 && (
          <motion.div key="aud" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                      className="max-w-5xl w-full">
            <div className="text-center text-[10px] uppercase tracking-[0.4em] font-semibold mb-6"
                 style={{ color: GOLD }}>What Altara delivers</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <AudCard Icon={User}       eyebrow="To consumers"    line="Trust becomes portable."      delay={0.2} />
              <AudCard Icon={Building2}  eyebrow="To institutions" line="Compliance becomes evidence." delay={0.65} />
              <AudCard Icon={Landmark}   eyebrow="To regulators"   line="Outcomes become observable."  delay={1.1} />
            </div>
          </motion.div>
        )}

        {phase === 3 && (
          <motion.div key="tag" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.7 }}
                      className="text-center">
            <TaglineReveal />
          </motion.div>
        )}

        {phase === 4 && (
          <motion.div key="sig" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                      className="text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
                   style={{ background: NAVY, border: `2px solid ${GOLD}` }}>
                <span className="font-display font-bold text-3xl" style={{ color: GOLD }}>A</span>
              </div>
              <div className="text-left">
                <div className="font-display text-4xl font-semibold text-white">Altara</div>
                <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: GOLD }}>
                  Consumer Intelligence
                </div>
              </div>
            </div>
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
                        transition={{ delay: 0.5, duration: 0.7 }}
                        className="h-px mx-auto mt-6 origin-center"
                        style={{ width: 320, background: GOLD, opacity: 0.5 }} />
            <div className="text-white/60 text-[13px] mt-4 tracking-wide">
              IOSCO Global TechSprint 2026 · Team Altara
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase mt-6" style={{ color: GOLD }}>
              altara.co
            </div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 0.8 }}
                        className="mt-8 text-[12px] text-white/50 italic max-w-lg mx-auto leading-relaxed">
              &ldquo;Every decision is evidence. Every consumer is a metric.&rdquo;
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-4 left-4">
        <button onClick={reset} data-testid="reset-closing"
                className="w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
                title="Reset">
          <RotateCcw className="w-3.5 h-3.5 text-white/70" />
        </button>
      </div>
    </div>
  );
}

function BuildLine({ text, delay, gold }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ delay, duration: 0.7, ease: "easeOut" }}
                className="font-display font-semibold leading-tight"
                style={{
                  color: gold ? GOLD : "white",
                  fontSize: gold ? "62px" : "58px",
                  letterSpacing: "-0.02em",
                }}>
      {text}
    </motion.div>
  );
}

function AudCard({ Icon, eyebrow, line, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay, duration: 0.5 }}
                className="rounded-2xl p-6 border text-center"
                style={{ background: "rgba(255,255,255,0.03)", borderColor: `${GOLD}44`,
                         backdropFilter: "blur(6px)" }}>
      <div className="w-11 h-11 rounded-full mx-auto flex items-center justify-center"
           style={{ background: `${GOLD}22` }}>
        <Icon className="w-5 h-5" style={{ color: GOLD }} />
      </div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-semibold mt-3" style={{ color: GOLD }}>
        {eyebrow}
      </div>
      <div className="font-display text-xl font-semibold text-white mt-2 leading-snug">
        {line}
      </div>
    </motion.div>
  );
}

function TaglineReveal() {
  const words = ["TRUST", "BEFORE", "TRANSACTION."];
  return (
    <div>
      <div className="flex items-baseline justify-center gap-4 flex-wrap">
        {words.map((w, i) => (
          <motion.span key={w}
            initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ delay: 0.3 + i * 0.35, duration: 0.6, ease: "easeOut" }}
            className="font-display font-semibold"
            style={{
              fontSize: 96,
              lineHeight: 1,
              letterSpacing: "-0.03em",
              color: w === "TRANSACTION." ? GOLD : "white",
            }}>
            {w}
          </motion.span>
        ))}
      </div>
      <motion.div initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }}
                  transition={{ delay: 1.8, duration: 0.8, ease: "easeOut" }}
                  className="h-1 mx-auto mt-8 origin-center rounded-full"
                  style={{ width: 400, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
    </div>
  );
}
